/** Reparaturen & Service: the workshop's care offering - repairs, bow service,
 *  sound adjustment, appraisals - with a three-step "how it works" strip and
 *  a link into the contact form (repair topic preselected).
 *
 *  Content lives in page_content (key "service") so it is editable in the
 *  admin Texte area. */

import { OutlineButtonLink } from '../components/ui'
import type { ContentBlock, PageContent } from '../lib/api'
import { useApi, useDocumentMeta, useHreflang, useLocale } from '../lib/hooks'
import { pagePaths, pathFor, t } from '../lib/i18n'

export default function Reparaturen() {
  const locale = useLocale()
  const strings = t(locale)
  const { data } = useApi<PageContent>(`/api/pages/service?locale=${locale}`)

  useDocumentMeta(data?.title || strings.meta.service.title, strings.meta.service.description)
  useHreflang(pagePaths('service'))

  const blocks: ContentBlock[] = data?.blocks ?? []
  const heading =
    blocks.find((b) => b.type === 'heading')?.text || strings.meta.service.heading
  const intro = blocks.find((b) => b.type === 'text')?.text ?? ''
  const cards = blocks.filter((b) => b.type === 'blurb')
  // The last text block: first line is the step-strip heading, rest are steps.
  const stepsBlock = blocks.filter((b) => b.type === 'text')[1]
  const stepLines = (stepsBlock?.text ?? '').split('\n').filter(Boolean)
  const stepsHeading = stepLines[0] ?? ''
  const steps = stepLines.slice(1)

  return (
    <>
      <section className="bg-paper px-6 pb-12 pt-16 md:px-10 md:pt-24" data-testid="service-head">
        <div className="mx-auto max-w-[1200px]">
          <p className="eyebrow m-0">{strings.nav.service}</p>
          <h1 className="m-0 mt-3 font-serif text-[clamp(40px,6vw,64px)] font-medium leading-[1.05] text-ink">
            {heading}
          </h1>
          {intro ? (
            <p className="mb-0 mt-6 max-w-[70ch] font-sans text-[19px] leading-[1.65] text-ink">{intro}</p>
          ) : null}
        </div>
      </section>

      {/* Service cards on the deeper paper tone, each with a brass top rule. */}
      <section className="bg-paper-deep px-6 py-16 md:px-10 md:py-24" data-testid="service-cards">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-6 md:grid-cols-2">
          {cards.map((card, index) => (
            <article
              key={index}
              className="rounded-[2px] border-t-[3px] border-amber bg-paper p-8 md:p-10"
              data-testid="service-card"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <h2 className="m-0 font-serif text-[28px] font-medium leading-[1.2] text-ink">{card.title}</h2>
                {card.badge ? (
                  <span
                    data-testid="service-badge"
                    className="inline-block rounded-full border border-amber/50 bg-amber/15 px-3 py-[3px] font-sans text-[12px] font-semibold uppercase tracking-[0.08em] text-ink"
                  >
                    {card.badge}
                  </span>
                ) : null}
              </div>
              <p className="mb-0 mt-4 font-sans text-[18px] leading-[1.65] text-ink">{card.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* How it works: three numbered steps, then the link into Kontakt. */}
      <section className="bg-paper px-6 py-16 md:px-10 md:py-24" data-testid="service-steps">
        <div className="mx-auto max-w-[1200px]">
          {stepsHeading ? (
            <h2 className="rule m-0 mb-12 font-serif text-[clamp(30px,3.6vw,40px)] font-medium leading-[1.1] text-ink">
              {stepsHeading}
            </h2>
          ) : null}
          <ol className="m-0 grid list-none grid-cols-1 gap-8 p-0 md:grid-cols-3">
            {steps.map((step, index) => (
              <li key={index} className="border-t border-line pt-6">
                <span className="block font-serif text-[40px] leading-none text-amber">{index + 1}</span>
                <p className="mb-0 mt-4 font-sans text-[18px] leading-[1.65] text-ink">{step}</p>
              </li>
            ))}
          </ol>
          <div className="mt-12">
            <OutlineButtonLink to={`${pathFor(locale, 'kontakt')}?thema=reparatur`}>
              {strings.nav.kontakt}
            </OutlineButtonLink>
          </div>
        </div>
      </section>
    </>
  )
}
